'use client';

import { useState } from 'react';
import { PhoneForwarded, User } from 'lucide-react';
import { Modal } from '@/shared/ui/Modal';
import { useAvailableStaff } from '../hooks/useAvailableStaff';
import type { TransferTarget } from '../types/calls.types';

interface TransferDialogProps {
  open: boolean;
  onClose: () => void;
  onTransfer: (staffId: string) => void;
  currentStaffId: string;
}

export function TransferDialog({
  open,
  onClose,
  onTransfer,
  currentStaffId,
}: TransferDialogProps) {
  const { data: staff = [], isLoading } = useAvailableStaff(open);
  const [selected, setSelected] = useState<string | null>(null);

  const transferable = staff.filter((s) => s.staffId !== currentStaffId && s.online);

  const handleTransfer = () => {
    if (selected) {
      onTransfer(selected);
      setSelected(null);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Transfer Call"
      size="sm"
      id="calls_transfer_dialog"
    >
      <div className="p-4 space-y-3">
        <p className="text-sm text-hms-textSub">
          Select an available staff member to transfer this call to.
        </p>

        {isLoading && (
          <div className="py-8 text-center text-sm text-hms-textDim">
            Loading available staff...
          </div>
        )}

        {!isLoading && transferable.length === 0 && (
          <div className="py-8 text-center text-sm text-hms-textDim">
            No other staff members are currently available.
          </div>
        )}

        <div className="space-y-1 max-h-64 overflow-y-auto">
          {transferable.map((s: TransferTarget) => (
            <button
              key={s.staffId}
              id={`calls_transfer_staff_${s.staffId}`}
              type="button"
              onClick={() => setSelected(s.staffId)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                selected === s.staffId
                  ? 'bg-hms-gold/10 border border-hms-gold'
                  : 'bg-hms-surface border border-transparent hover:bg-hms-card'
              }`}
            >
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-hms-card">
                <User className="w-4 h-4 text-hms-textSub" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-hms-text truncate">
                  {s.name}
                </p>
                <p className="text-xs text-hms-textDim">{s.role} — {s.dept}</p>
              </div>
              <span className="w-2 h-2 rounded-full bg-hms-green flex-shrink-0" />
            </button>
          ))}
        </div>

        <div className="flex gap-3 pt-2">
          <button
            id="calls_transfer_cancel_btn"
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-lg text-sm font-medium border border-hms-border text-hms-textSub hover:bg-hms-surface transition-colors"
          >
            Cancel
          </button>
          <button
            id="calls_transfer_confirm_btn"
            type="button"
            onClick={handleTransfer}
            disabled={!selected}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-hms-gold text-white hover:bg-hms-gold/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PhoneForwarded className="w-4 h-4" />
            Transfer
          </button>
        </div>
      </div>
    </Modal>
  );
}
